import { FC, PropsWithChildren, ReactNode, useCallback, useEffect, useRef } from 'react'

import { useModalInstanceOptional } from '@context/ModalInstanceContext'
import { useModalsOptional } from '@context/ModalsContext'

import { SearchFieldProps } from '@ui/SearchField/SearchField'
import { Direction } from '@ui/common/layout.types'
import { PopoverContent } from '@ui/components/Popover/PopoverContent/PopoverContent'

import { useWindowResize } from '@utils/domUtils'

type ModalRenderContext = {
  search: string
  selectedIds: string[]
  toggle: (id: string) => void
  setSelectedIds: (ids: string[]) => void
}

export interface PopoverProps {
  direction?: Direction
  distance?: string
  contentClassName?: string
  isPagination?: boolean
  isMultiSelect?: boolean
  disabled?: boolean
  isOpen: boolean
  content: ReactNode
  setIsOpen: (isOpen: boolean) => void
  isWidthFixed?: boolean
  label?: string
  modalHeaderSearch?: SearchFieldProps
  modalFooter?: ReactNode | ((close: () => void) => ReactNode)
  modalCanSearch?: boolean
  modalInitialSelectedIds?: string[]
  onApplyOnClose?: (selectedIds: string[]) => void
  modalRender?: (ctx: ModalRenderContext) => ReactNode
  onModalSearchChange?: (value: string) => void
}

const useOnESCEvent = (isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen) return

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleEscKey)
    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [isOpen, onClose])
}

export const Popover: FC<PropsWithChildren<PopoverProps>> = (props) => {
  const {
    children,
    isOpen,
    isMultiSelect,
    isPagination,
    direction = 'top',
    distance,
    contentClassName,
    content,
    setIsOpen,
    disabled = false,
    isWidthFixed = false,
    label,
    modalHeaderSearch,
    modalFooter,
    modalCanSearch,
    modalInitialSelectedIds,
    onApplyOnClose,
    modalRender,
    onModalSearchChange,
  } = props

  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const isMobile = useWindowResize()
  const modalInstance = useModalInstanceOptional()
  const modalsCtx = useModalsOptional()
  const hasOpenModal = (modalsCtx?.modals.length ?? 0) > 0
  const isInInteractiveModal = !!modalInstance?.isInteractive
  const blockedByNestedModal = !!modalInstance && !modalInstance.isInteractive
  const isSuspended = blockedByNestedModal || (hasOpenModal && !isInInteractiveModal)

  useEffect(() => {
    if (!modalInstance) return
    if (isMobile) return

    if (!modalInstance.isInteractive && isOpen) {
      setIsOpen(false)
    }
  }, [modalInstance, isOpen, setIsOpen, isMobile])

  const onClose = useCallback(() => setIsOpen(false), [setIsOpen])

  useOnESCEvent(!isMobile && isOpen && !isSuspended, onClose)

  const triggerWidth = triggerRef?.current?.getBoundingClientRect().width

  return (
    <div ref={triggerRef} onClick={() => !disabled && !isMultiSelect && setIsOpen(!isOpen)}>
      {children}
      <PopoverContent
        label={label}
        isOpen={isOpen}
        direction={direction}
        distance={distance}
        contentClassName={contentClassName}
        onClose={onClose}
        isMultiSelect={isMultiSelect}
        ref={containerRef}
        isWidthFixed={isWidthFixed}
        matchTriggerWidth={triggerWidth}
        isPagination={isPagination}
        triggerElement={triggerRef.current}
        modalHeaderSearch={modalHeaderSearch}
        modalFooter={modalFooter}
        modalCanSearch={modalCanSearch}
        modalInitialSelectedIds={modalInitialSelectedIds}
        onApplyOnClose={onApplyOnClose}
        modalRender={modalRender}
        onModalSearchChange={onModalSearchChange}
        isSuspended={isSuspended}
      >
        {content}
      </PopoverContent>
    </div>
  )
}
